import React from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import {
  ArrowRight,
  Brain,
  Eye,
  Dna,
  ChevronDown,
  Globe,
  Users,
  Zap,
  Heart,
  Building2,
  Factory,
  Briefcase,
  Home,
  Shield,
  TrendingUp,
  Target,
  CheckCircle2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { TrustMetrics } from "../TrustMetrics";
import {
  FadeIn,
  SlideIn,
  StaggerContainer,
  StaggerItem,
} from "../PageTransition";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { TestimonialsSection } from "../TestimonialsSection";

export type ServiceCardProps = {
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  href: string;
  color?: string; // hex like "#00CFFF"
};

export const HomePage: React.FC = () => {
  const services = [
    {
      title: "Panaceon",
      description:
        "Transform medical coding operations with autonomous agentic AI. Panaceon processes clinical charts using specialized ICD-10, CPT, and HCPCS coding agents, achieving ≥90% confidence scoring for auto-approval while routing exceptions for human review with audit-ready citations.",
      icon: Brain,
      href: "/products/ai/Panaceon",
      color: "#00CFFF",
    },
    {
      title: "MVSxAI",
      description:
        "MVS x AI is a computer vision safety guard that analyzes camera feeds 24/7, detecting missing or incorrect safety gear and triggering instant alerts to stop incidents before escalation. Designed for industrial sites, it ensures safety protocols are followed, reduces accidents, and minimizes regulatory fines. ",
      icon: Eye,
      href: "/products/ai/mvsxAi",
      color: "#0FFFA0",
    },
    {
      title: "Tawjih x AI",
      description:
        "Tawjih AI is a real-time AI engine that scores customer data to surface tailored financial products. It automates sales tasks and feeds a unified dashboard, lifting conversion and cutting operational cost for banks. By leveraging customer data in real time, Tawjih AI enhances product recommendations, streamlines sales workflows, and boosts overall efficiency for financial institutions.",
      icon: Dna,
      href: "/products/ai/tawjihai",
      color: "#8A3FFC",
    },
  ];

  function ServiceCard({
    title,
    description,
    icon: Icon,
    href,
    color = "#0EA5E9", // fallback accent
  }: ServiceCardProps) {
    // Use 8-digit hex (#RRGGBBAA) for a soft icon background
    const softBg = `${color}1A`;
    return (
      <Link
        to={href}
        className="group block h-full"
        style={
          {
            // expose the accent as a CSS var for reuse
            // @ts-ignore - custom property
            "--accent": color,
          } as React.CSSProperties
        }
      >
        <div className="h-full flex flex-col rounded-2xl border-3 bg-gray/60 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
          {/* Top border accent */}
          <div className="h-1 w-full" />

          {/* Card content */}
          <div className="flex-1 p-6 flex flex-col gap-4">
            <div
              className="inline-flex size-12 items-center justify-center rounded-xl"
              style={{ background: softBg }}
            >
              <Icon className="size-6" style={{ color }} />
            </div>

            <h3 className="text-lg font-semibold leading-tight">
              {title}
            </h3>
            <p className="text-sm text-neutral-400 flex-1">
              {description}
            </p>

            <div className="pt-2">
              <span
                className="inline-flex items-center gap-2 text-sm font-medium"
                style={{ color }}
              >
                Learn more{" "}
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[80vh] flex items-center overflow-hidden">
        {/* Ambient gradient orbs */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -top-24 -left-32 h-[36rem] w-[36rem] rounded-full blur-3xl opacity-40"
          style={{
            background:
              "radial-gradient(50% 50% at 50% 50%, rgba(138,63,252,0.55) 0%, rgba(138,63,252,0.0) 70%)",
          }}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.5 }}
          transition={{ duration: 1.2 }}
        />
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 -right-40 h-[42rem] w-[42rem] rounded-full blur-3xl opacity-40"
          style={{
            background:
              "radial-gradient(50% 50% at 50% 50%, rgba(14,165,233,0.55) 0%, rgba(14,165,233,0.0) 70%)",
          }}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.5 }}
          transition={{ duration: 1.2, delay: 0.15 }}
        />

        {/* Subtle grid overlay */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.7) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage:
              "radial-gradient(ellipse at center, black 40%, transparent 75%)",
          }}
        />

        {/* Soft diagonal wash */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center"
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-3 py-1 text-xs md:text-sm text-muted-foreground mb-6"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <span className="inline-block h-2 w-2 rounded-full bg-primary" />
              Now in{" "}
              <span className="font-semibold text-foreground">
                India,
              </span>{" "}
              <span className="font-semibold text-foreground">
                Oman,
              </span>{" "}
              <span className="font-semibold text-foreground">
                Dubai
              </span>
              &{" "}
              <span className="font-semibold text-foreground">
                UK
              </span>
            </motion.div>

            {/* Headline */}
            <div className="mb-6 leading-[0.95]">
              <motion.h1
                className="text-4xl md:text-4xl lg:text-4xl font-extrabold tracking-tight"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35, duration: 0.6 }}
              >
                <motion.span
                  className="block text-foreground"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.45, duration: 0.6 }}
                >
                  AI-Driven
                </motion.span>
                <motion.span
                  className="block bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                >
                  Solutions for Smarter Decisions
                </motion.span>
              </motion.h1>
            </div>

            {/* Subheadline */}
            <motion.p
              className="text-md  text-muted-foreground text-center max-w-3xl mx-auto mb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              Driving the future of industries with powerful AI
              solutions from predictive insights to
              bioinformatics where innovation meets results.
            </motion.p>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.6 }}
            >
              <Button
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/80 text-primary-foreground neon-glow px-8 py-4 text-lg group"
              >
                <Link to="/solutions">
                  <span>Explore Solutions</span>
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-accent/60 text-accent hover:bg-accent/10 px-8 py-4 text-lg"
              >
                <Link to="/contact">Talk to an Expert</Link>
              </Button>

              <Button
                asChild
                variant="ghost"
                size="lg"
                className="text-muted-foreground hover:text-foreground px-6 py-4"
              >
                <Link
                  to="/case-studies"
                  className="inline-flex items-center"
                >
                  View Case Studies
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator (unchanged) */}
        <motion.button
          onClick={() =>
            window.scrollTo({
              top: window.innerHeight,
              behavior: "smooth",
            })
          }
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-primary hover:text-accent transition-colors cursor-pointer"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8" />
        </motion.button>
      </section>

      {/* Solutions Across Critical Industries Section */}
      <section className="py-15 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="mb-4">
                Solutions Across Critical Industries
              </h2>
            </div>
          </FadeIn>

          <StaggerContainer>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Zap,
                  title: "Energy & Utilities",
                  features: [
                    "Solar monitoring with AI optimization (AIOSLR)",
                    " Green hydrogen plant intelligence (Green H2)",
                    " Smart HVAC optimization (AI Cooler)",
                    " Battery energy storage management",
                  ],
                  metric: "30%",
                  metricLabel: "Energy Cost Reduction",
                  link: "/products/ai/greenh2",
                  color: "#00CFFF",
                },
                {
                  icon: Heart,
                  title: "Healthcare & Life Sciences",
                  features: [
                    "Medical coding automation (Panaceon)",
                    " Genomics analysis platform (GeneSeqer)",
                    " Patient engagement AI (WhispeRAGon)",
                    " Clinical workflow optimization",
                  ],
                  metric: "47%",
                  metricLabel: "Coding Accuracy Improvement",
                  link: "/products/ai/panaceon",
                  color: "#0FFFA0",
                },
                {
                  icon: Building2,
                  title: "Oil & Gas",
                  features: [
                    "Real-time safety compliance (MVS x AI)",
                    "Predictive maintenance for drilling",
                    " Satellite asset tracking",
                    "Incident prevention analytics",
                  ],
                  metric: "95%",
                  metricLabel: "Coding Accuracy",
                  link: "/products/ai/financepolicycheck",
                  color: "#8A3FFC",
                },
                {
                  icon: Factory,
                  title: "Manufacturing",
                  features: [
                    "Home appliance MES (APLIX)",
                    " Workplace safety monitoring (MVS x AI)",
                    " Equipment failure prediction",
                    " Quality control automation",
                  ],
                  metric: "50%",
                  metricLabel: "Downtime Reduction",
                  link: "/products/ai/predictivemaintenance",
                  color: "#FF3D9A",
                },
                {
                  icon: Briefcase,
                  title: "Banking & Financial Services",
                  features: [
                    "Customer intelligence platform (Tawjih x AI)",
                    "• Compliance automation (Finance Policy Check)",
                    "• Crypto trading infrastructure",
                    "• Risk scoring engines",
                  ],
                  metric: "40%",
                  metricLabel: "Fraud Detection Accuracy",
                  link: "/products/ai/financepolicycheck",
                  color: "#FFB800",
                },
                {
                  icon: Home,
                  title: "Construction & Real Estate",
                  features: [
                    "PPE compliance monitoring (MVS x AI)",
                    "Project planning AI (Planfusion)",
                    "Property intelligence platform",
                    "Asset lifecycle management",
                  ],
                  metric: "35%",
                  metricLabel: "Project Efficiency Gain",
                  link: "/products/ai/realestateai",
                  color: "#4D9FFF",
                },
              ].map((industry, index) => (
                <StaggerItem key={industry.title + index}>
                  <Card
                    className="glass-strong h-full hover:shadow-lg transition-all duration-300 group border-l-4"
                    style={{ borderLeftColor: industry.color }}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-6">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: `${industry.color}20`,
                          }}
                        >
                          <industry.icon
                            className="w-5 h-5"
                            style={{ color: industry.color }}
                          />
                        </div>

                        <h3 className="text-lg font-bold flex-1 ml-4 text-left">
                          {industry.title}
                        </h3>
                      </div>

                      <ul className="space-y-2 mb-6">
                        {industry.features.map(
                          (feature, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-2 text-sm text-muted-foreground"
                            >
                              <CheckCircle2
                                className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                                style={{
                                  color: industry.color,
                                }}
                              />
                              <span>{feature}</span>
                            </li>
                          ),
                        )}
                      </ul>

                      <Link
                        to={industry.link}
                        className="inline-flex items-center gap-2 text-xs font-medium group-hover:translate-x-1 transition-transform"
                        style={{ color: industry.color }}
                      >
                        Explore{" "}
                        {industry.title.split("&")[0].trim()}{" "}
                        Solutions →
                      </Link>
                    </CardContent>
                  </Card>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* Key Products Driving Industry Transformation */}
      <section className="py-15 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="mb-4">
                Key Products Driving Industry Transformation
              </h2>
            </div>
          </FadeIn>

          <StaggerContainer>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  name: "Panaceon",
                  tagline: "Autonomous Medical Coding Engine",
                  description:
                    "Autonomous Medical Coding Engine -> Autonomous Medical Coding, Billing and Claim Engine.",
                  tags: [
                    "Healthcare",
                    "Automation",
                    "Compliance",
                  ],
                  link: "/products/ai/panaceon",
                },
                {
                  name: "Planfusion",
                  tagline: "Intelligent Project Planning Platform",
                  description:
                    "AI-powered project management solution that streamlines planning, resource allocation, and team collaboration for enhanced productivity.",
                  tags: ["Planning", "Collaboration", "AI"],
                  link: "/products/ai/planfusion",
                },
                {
                  name: "ManageRTC",
                  tagline: "Real-Time Communication Manager",
                  description:
                    "Advanced communication platform that enables seamless real-time collaboration, monitoring, and control across distributed teams and systems.",
                  tags: ["Communication", "Real-time", "Management"],
                  link: "/products/ai/managertc",
                },
                {
                  name: "StyleSync AI",
                  tagline: "AI-Powered Design Synchronization",
                  description:
                    "Intelligent fashion design system that maintains brand consistency across multiple e-com & boutique platforms by synchronizing styles, components, and assets for virtual try-on.",
                  tags: ["Design", "Automation", "Branding"],
                  link: "/products/ai/stylesyncai",
                },
              ].map((product, index) => (
                <StaggerItem key={product.name}>
                  <Card className="glass-strong h-full hover:shadow-lg transition-all duration-300 group">
                    <CardContent className="p-6 flex flex-col h-full">
                      <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                        {product.name}
                      </h3>

                      <p className="text-sm text-primary mb-4">
                        {product.tagline}
                      </p>

                      <p className="text-base text-muted-foreground text-left mb-6 flex-1">
                        {product.description}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-6">
                        {product.tags.map((tag) => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="text-xs"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      <Link
                        to={product.link}
                        className="inline-flex items-center gap-2 text-sm font-medium text-primary group-hover:translate-x-1 transition-transform"
                      >
                        Learn More
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </CardContent>
                  </Card>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* Global Reach Section */}
      <section className="py-15 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-[#111111]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <FadeIn>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center mb-4">
                <Globe className="w-8 h-8 text-primary mr-3" />
                <h2 className="mb-0">Global Reach</h2>
              </div>
              <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
                Building the future through strategic
                partnerships and academic collaboration
                worldwide
              </p>
            </div>
          </FadeIn>

          {/* NVIDIA Inception Program Badge */}
          <FadeIn>
            <div className="mb-16 flex flex-col items-center">
              <motion.div
                className="glass-strong rounded-2xl p-4 md:p-6 inline-block border border-primary/20 hover:border-primary/40 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                   src="/assets/global-reach/inceptionProgram.jpg"
                  alt="NVIDIA Inception Program"
                  className="max-w-full h-auto w-full md:w-[350px]"
                />
              </motion.div>
              <p className="text-base text-muted-foreground text-center mt-4 max-w-2xl">
                We are proud to partner with NVIDIA and to contribute as part of NVIDIA's Inception program
              </p>
            </div>
          </FadeIn>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Strategic Partnerships */}
            <div>
              <FadeIn>
                <div className="text-center mb-12">
                  <div className="flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-accent mr-2" />
                    <h3 className="text-2xl font-bold">
                      Strategic Partnerships
                    </h3>
                  </div>
                  <p className="text-muted-foreground text-center">
                    Collaborating with industry leaders to drive
                    innovation forward
                  </p>
                </div>
              </FadeIn>

              <StaggerContainer>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    
                    {
                      name: "E & E Business Solutions",
                      logo: "/assets/global-reach/e_ebs.jpg",
                      website:
                        "https://www.linkedin.com/company/e-ebusinesssolutions/posts/?feedView=all",
                    },
                    {
                      name: "ZOLAA",
                      logo: "/assets/global-reach/Zolaa.png",
                      website:
                        "https://www.facebook.com/zolaatechnologies/",
                    },
                    {
                      name: "PACHA H2",
                      logo: "/assets/global-reach/pachah2.png",
                      // website: "https://pachah2.com/",
                    },
                    {
                      name: "MAJAN H2",
                      logo: "/assets/global-reach/majanh2.png",
                      // website: "https://majanh2.com/",
                    },
                  ].map((partner) => (
                    <StaggerItem key={partner.name}>
                      <motion.a
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block group"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Card className="glass-strong h-full hover:border-primary/50 transition-all duration-300 overflow-hidden">
                          <CardContent className="p-6 text-center">
                            <div className="relative mb-4 mx-auto w-24 h-24 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                              <ImageWithFallback
                                src={partner.logo}
                                alt={`${partner.name} logo`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <h4 className="font-bold text-lg group-hover:text-primary transition-colors">
                              {partner.name}
                            </h4>
                          </CardContent>
                        </Card>
                      </motion.a>
                    </StaggerItem>
                  ))}
                </div>
              </StaggerContainer>
            </div>

            {/* Academic Partners */}
            <div>
              <FadeIn>
                <div className="text-center mb-12">
                  <div className="flex items-center justify-center mb-4">
                    <Brain className="w-6 h-6 text-secondary mr-2" />
                    <h3 className="text-2xl font-bold">
                      Academic Partners
                    </h3>
                  </div>
                  <p className="text-muted-foreground text-center">
                    Advancing research and education through
                    academic excellence
                  </p>
                </div>
              </FadeIn>

              <StaggerContainer>
                <div className="grid grid-cols-6 auto-rows-fr gap-2 sm:gap-3 lg:gap-4">
                  {[
                    {
                      name: "VIT",
                      fullName:
                        "Vellore Institute of Technology",
                      logo: "/assets/global-reach/vit.png",
                      website: "https://vit.ac.in/",
                    },
                    {
                      name: "MIT",
                      fullName:
                        "MIT College of Arts and Science for Women",
                      logo: "/assets/global-reach/MIT.jpeg",
                      website: "https://www.mitindia.edu/",
                    },
                    {
                      name: "SASURIE INSTITUTIONS",
                      fullName:
                        "Sasurie College of Engineering",
                      logo: "/assets/global-reach/sasurie.png",
                      website: "https://sasurieengg.com/",
                    },
                    {
                      name: "EASC",
                      fullName: "Erode Arts & Science College",
                      logo: "/assets/global-reach/erodeArts.png",
                      website: "https://www.easc.ac.in/",
                    },
                    {
                      name: "EXCEL",
                      fullName: "Excel Engineering College",
                      logo: "/assets/global-reach/excel.jpeg",
                      website:
                        "https://www.excelinstitutions.com/",
                    },
                  ].map((partner, index, partners) => {
                    const remainder = partners.length % 3;
                    const centerSingleCard =
                      remainder === 1 && index === partners.length - 1;
                    const centerTwoCardRow =
                      remainder === 2 && index === partners.length - 2;
                    const partnerCard = (
                      <Card className="glass-strong h-full hover:border-secondary/50 transition-all duration-300 overflow-hidden">
                        <CardContent className="flex h-full min-h-[126px] flex-col items-center gap-2 p-2 pt-4 text-center sm:min-h-[154px] sm:gap-3 sm:p-4 lg:min-h-[172px]">
                          <div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white p-1 sm:h-16 sm:w-16 lg:h-20 lg:w-20">
                            <ImageWithFallback
                              src={partner.logo}
                              alt={`${partner.name} logo`}
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <div className="min-w-0">
                            <h4 className="break-words text-[11px] font-bold leading-tight group-hover:text-secondary transition-colors sm:text-sm lg:text-base">
                              {partner.name}
                            </h4>
                            <p className="mx-auto mt-1 hidden max-w-[10rem] text-center text-[10px] leading-snug text-muted-foreground sm:block lg:text-xs">
                              {partner.fullName}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    );

                    return (
                      <motion.div
                        key={partner.name}
                        className={`col-span-2 ${
                          centerSingleCard ? "col-start-3" : ""
                        } ${
                          centerTwoCardRow ? "col-start-2" : ""
                        }`}
                        variants={{
                          hidden: { opacity: 0, y: 30 },
                          visible: { opacity: 1, y: 0 },
                        }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                      >
                        {partner.website ? (
                          <motion.a
                            href={partner.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block h-full group"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.98 }}
                            transition={{ duration: 0.2 }}
                          >
                            {partnerCard}
                          </motion.a>
                        ) : (
                          <motion.div
                            className="h-full group"
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.2 }}
                          >
                            {partnerCard}
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </StaggerContainer>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by Industry Leaders */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <FadeIn>
            <TrustMetrics />
          </FadeIn>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="py-15 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-background to-[#0A0A0A]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Left Side - Our Services Content */}
            <div className="lg:col-span-4">
              <FadeIn>
                <div>
                  <h2 className="mb-6 text-center">
                    Our Services
                  </h2>
                  <p className="text-base text-muted-foreground text-center leading-relaxed">
                    At{" "}
                    <span className="amasqis-text font-semibold">
                      amasQIS
                    </span>
                    , we deliver intelligent, scalable, and
                    future-ready technology solutions that help
                    businesses innovate, grow, and operate
                    efficiently. Our expertise spans Artificial
                    Intelligence, IT Services, and IoT
                    Solutions, along with end-to-end support for
                    existing systems.
                  </p>
                </div>
              </FadeIn>
            </div>

            {/* Right Side - Service Cards */}
            <div className="lg:col-span-8">
              <StaggerContainer>
                <div className="grid sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4">
                  {[
                    {
                      title: "AI Solutions",
                      description:
                        "Transform data into actionable insights with custom AI & ML models, predictive analytics, generative AI, and intelligent automation.",
                      icon: Brain,
                      color: "#00CFFF",
                      link: "/solutions",
                    },
                    {
                      title: "IT Services",
                      description:
                        "Ensure stable, secure, and high-performing technology environments with end-to-end application development and support.",
                      icon: Briefcase,
                      color: "#0FFFA0",
                      link: "/solutions",
                    },
                    {
                      title: "IoT Solutions",
                      description:
                        "Build smart IoT ecosystems that connect devices, data, and platforms for real-time monitoring and control.",
                      icon: Zap,
                      color: "#8A3FFC",
                      link: "/solutions",
                    },
                    {
                      title: "Project Support",
                      description:
                        "Enhance existing projects with implementation support, feature optimization, and dedicated team augmentation.",
                      icon: Target,
                      color: "#FF3D9A",
                      link: "/solutions",
                    },
                  ].map((service, index) => (
                    <StaggerItem key={service.title}>
                      <motion.div
                        className="relative group h-full"
                        whileHover={{ y: -4 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="h-full rounded-xl border border-white/10 bg-[#1A1A1A]/80 backdrop-blur-sm p-4 flex flex-col items-center text-center">
                          {/* Icon */}
                          <div
                            className="w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300"
                            style={{
                              backgroundColor: `${service.color}20`,
                              boxShadow: `0 4px 20px ${service.color}30`,
                            }}
                          >
                            <service.icon
                              className="w-8 h-8"
                              style={{ color: service.color }}
                            />
                          </div>

                          {/* Title */}
                          <h4
                            className="font-bold text-lg mb-3"
                            style={{ color: service.color }}
                          >
                            {service.title}
                          </h4>

                          {/* Description */}
                          <p className="text-sm text-muted-foreground text-center">
                            {service.description}
                          </p>
                        </div>
                      </motion.div>
                    </StaggerItem>
                  ))}
                </div>
              </StaggerContainer>
            </div>
          </div>
        </div>
      </section>

      {/* Why Industry Leaders Choose amasQIS.ai */}
      <section className="py-15 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="mb-4">
                Why Industry Leaders Choose{" "}
                <span className="amasqis-text">amasQIS.ai</span>
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: "Industry-Specific AI Models",
                description:
                  "Purpose-built AI models trained on industry-specific data, delivering higher accuracy and faster deployment than generic solutions.",
                color: "#00CFFF",
              },
              {
                icon: TrendingUp,
                title: "Rapid Deployment for Results",
                description:
                  "Production-ready systems deployed in weeks, not months. See measurable results within the first quarter of implementation.",
                color: "#0FFFA0",
              },
              {
                icon: Target,
                title: "Measurable ROI in 90 Days",
                description:
                  "Track concrete business metrics from day one. Our solutions deliver documented cost savings and efficiency gains within 90 days.",
                color: "#8A3FFC",
              },
              {
                icon: Globe,
                title: "Global Reach, Local Expertise",
                description:
                  "Operating across India, Oman, and Dubai with deep understanding of regional compliance and business requirements.",
                color: "#FF3D9A",
              },
            ].map((feature, index) => (
              <SlideIn
                key={feature.title}
                direction="up"
                delay={index * 0.1}
              >
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                    style={{
                      backgroundColor: `${feature.color}20`,
                    }}
                  >
                    <feature.icon
                      className="w-8 h-8"
                      style={{
                        color: feature.color,
                      }}
                    />
                  </div>
                  <h3 className="text-lg font-bold mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-base text-muted-foreground text-center">
                    {feature.description}
                  </p>
                </div>
              </SlideIn>
            ))}
          </div>
        </div>
      </section>

      {/* Blue CTA Section */}

      {/* Services Section */}

      {/* CTA Section */}
      <section className="py-15 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/5 to-secondary/10" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <h2 className="mb-6">
              Ready to Transform Your Operations?
            </h2>
            <p className="text-base text-muted-foreground mb-8 text-center max-w-2xl mx-auto">
              Schedule a consultation to discuss your specific
              challenges and explore how{" "}
              <span className="amasqis-text font-semibold">
                amasQIS.ai
              </span>{" "}
              can deliver measurable impact.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/80 text-primary-foreground neon-glow px-8 py-4 text-lg"
              >
                <Link to="/contact">Start Your AI Journey</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-accent text-accent hover:bg-accent/10 px-8 py-4 text-lg"
              >
                <Link to="/case-studies">View Case Studies</Link>
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};
